import { z } from "zod";
export const connectorConfigTimestampSchema = z
    .number()
    .int()
    .nonnegative()
    .max(Number.MAX_SAFE_INTEGER);
export const connectorConfigScopeSchema = z
    .string()
    .trim()
    .min(1, "scope must not be empty")
    .max(10_000, "scope is too long")
    .refine((value) => !/[\u0000\r\n]/u.test(value), {
    message: "scope must be a single line without control characters",
});
export const connectorOauthRefreshConfigShape = {
    oauthReconnectRequiredAt: connectorConfigTimestampSchema.optional(),
    oauthRefreshedAt: connectorConfigTimestampSchema.optional(),
};
export const connectorUpdatedAtConfigShape = {
    updatedAt: connectorConfigTimestampSchema.optional(),
};
export const userScopedConnectorSystemManagedConfigShape = {
    userContextId: boundedConnectorConfigString("userContextId", 512),
};
export function boundedConnectorConfigString(label, max = 1_024) {
    return z
        .string()
        .trim()
        .min(1, `${label} must not be empty`)
        .max(max, `${label} is too long`);
}
export function httpConnectorConfigUrl(label, options = {}) {
    return z
        .string()
        .trim()
        .min(1, `${label} must not be empty`)
        .max(2_048, `${label} is too long`)
        .superRefine((value, ctx) => {
        let url;
        try {
            url = new URL(value);
        }
        catch {
            ctx.addIssue({
                code: "custom",
                message: `${label} must be an absolute HTTP(S) URL`,
            });
            return;
        }
        if (url.protocol !== "http:" && url.protocol !== "https:") {
            ctx.addIssue({ code: "custom", message: `${label} must use HTTP or HTTPS` });
        }
        if (options.httpsOrLoopback &&
            url.protocol === "http:" &&
            !["localhost", "127.0.0.1", "[::1]"].includes(url.hostname)) {
            ctx.addIssue({
                code: "custom",
                message: `${label} must use HTTPS except for a loopback development endpoint`,
            });
        }
        if (url.username || url.password) {
            ctx.addIssue({
                code: "custom",
                message: `${label} must not contain embedded credentials`,
            });
        }
        if (url.hash || (!options.allowQuery && url.search)) {
            ctx.addIssue({
                code: "custom",
                message: `${label} must not contain a query string or fragment`,
            });
        }
        if (options.originOnly &&
            (url.pathname !== "" && url.pathname !== "/")) {
            ctx.addIssue({ code: "custom", message: `${label} must be an origin without a path` });
        }
    });
}
export function uniqueConnectorConfigStrings(label, maxItems) {
    return z
        .array(boundedConnectorConfigString(label, 1_024))
        .max(maxItems, `${label} contains too many entries`)
        .superRefine((values, ctx) => {
        const seen = new Set();
        for (const [index, value] of values.entries()) {
            if (seen.has(value)) {
                ctx.addIssue({
                    code: "custom",
                    message: `${label} must not contain duplicate entries`,
                    path: [index],
                });
            }
            seen.add(value);
        }
    });
}
export function delimitedConnectorConfigString(label, options) {
    const separator = options.separator ?? /,/u;
    return boundedConnectorConfigString(label, 10_000).superRefine((value, ctx) => {
        const parts = value.split(separator).map((item) => item.trim());
        if (parts.some((item) => item.length === 0)) {
            ctx.addIssue({ code: "custom", message: `${label} contains an empty entry` });
        }
        if (parts.length > options.maxItems) {
            ctx.addIssue({ code: "custom", message: `${label} contains too many entries` });
        }
        if (new Set(parts).size !== parts.length) {
            ctx.addIssue({ code: "custom", message: `${label} must not contain duplicate entries` });
        }
    });
}
export const emptyConnectorConfigSchema = z.object({}).strict();
